/**
 * Web's outbound message actions — dismiss, reply, action.
 *
 * Web equivalent of the extension's handleDismiss / handleDismissAll /
 * handleReply / handleAction in apps/extension/src/background/index.ts. The
 * extension routes these through the background service worker
 * because its popup/sidepanel can be torn down mid-flight; the web
 * app has a single React tree, so the same calls can run inline
 * from the page.
 *
 * The single-card `dismissNotification` path is intentionally NOT batched (for
 * single-card dismisses the extension debounces its storage write because it
 * expects rapid dismiss floods; its `handleDismissAll` bypasses that queue).
 * Web v1 has a single tab; the rare double-dismiss isn't worth the complexity.
 * `dismissAllNotifications` (Clear All) uses bounded bulk server requests.
 */

import { pickAndroidMsgId } from "@reflecto/client-core";
import {
  type ActionPayload,
  decodeBase64,
  encodeBase64,
  encryptMessage,
  type ReplyPayload,
} from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import { actionMessage, dismissMessage, dismissMessages, replyMessage } from "../api/client";
import { messages } from "../i18n";
import { logError } from "../lib/log";
import { trackDismissIntent, trackReplySent } from "./analytics";
import { conversations } from "./conversationsStore";
import { dropNotificationFromStorage, dropNotificationsFromStorage } from "./notificationsStore";

export interface SendResult {
  success: boolean;
  error?: string;
}

/**
 * Extract a human-readable error string from a `catch` value. The
 * `catch` clause receives `unknown` — the thrown value can be a
 * string, a plain object, `null`, or anything else. Surfacing
 * `(err as Error).message` blindly produces `undefined` when the
 * value isn't an Error instance, which is indistinguishable from
 * "no error detail" in the UI. Coerce non-Error values via
 * `String()` so the inline feedback always has SOMETHING useful
 * to show.
 */
function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Dismiss a single notification.
 *
 * Two-step: notify the server (which forwards to Android via FCM),
 * then drop the card from local storage. Order matters — if the
 * server call rejects (e.g. expired token) we keep the card so the
 * user can retry. Storage write happens after the network ack so
 * the UI doesn't optimistically remove a card that's still on the
 * paired device.
 */
export async function dismissNotification(msgId: string): Promise<SendResult> {
  try {
    const { token, androidDeviceId, notifications } = await webStorage.get(
      "token",
      "androidDeviceId",
      "notifications"
    );
    if (!token || !androidDeviceId) return { success: false, error: messages.errors.notPaired };
    const androidMsgId = pickAndroidMsgId(msgId, notifications);
    await dismissMessage(token, androidMsgId, androidDeviceId);
    // Local storage drop uses the card's stable msgId, NOT the Android-side latest.
    await dropNotificationFromStorage(msgId);
    trackDismissIntent(1);
    return { success: true };
  } catch (err) {
    logError("messages", "dismiss_failed", err);
    return { success: false, error: errorMessage(err) };
  }
}

/**
 * Dismiss an entire conversation thread by key. Calls the server
 * dismiss for the latest message in the thread (matches the contract
 * Android expects — there's no per-thread dismiss endpoint), then
 * removes the thread from local storage via the conversations module
 * (which also updates the reverse index + global unread counter).
 */
export async function dismissConversation(key: string): Promise<SendResult> {
  try {
    const {
      token,
      androidDeviceId,
      conversations: store,
    } = await webStorage.get("token", "androidDeviceId", "conversations");
    if (!token || !androidDeviceId) return { success: false, error: messages.errors.notPaired };
    const thread = store?.[key];
    if (!thread) return { success: true }; // Already gone — treat as no-op success.
    const latestMsg = thread.messages[thread.messages.length - 1];
    if (latestMsg) {
      await dismissMessage(token, latestMsg.msgId, androidDeviceId);
    }
    await conversations.removeConversation(key);
    trackDismissIntent(1);
    return { success: true };
  } catch (err) {
    logError("messages", "conversation_dismiss_failed", err);
    return { success: false, error: errorMessage(err) };
  }
}

/**
 * Bulk "Clear All": remove every listed regular notification + conversation
 * thread, then issue bounded batched dismiss requests. Replaces the old
 * per-card loop that fired N parallel `DELETE`s — on a multi-day backlog that
 * storm tripped infra rate-limiting, the failures left cards un-dismissed
 * ("always stale left").
 *
 * Optimistic-first (opposite of `dismissNotification`, which is network-first):
 * clearing hundreds shouldn't block the UI on one round-trip. Local removal is
 * two sequential bulk writes (regulars + threads), not one atomic write like the
 * extension's `handleDismissAll` (see §web-storage-atomicity follow-up). No rollback
 * on failure (don't-roll-back-on-gone): the cards stay removed locally — the
 * user asked to clear them and the single request rarely fails. This is a
 * one-way local clear on failure: `/sync` gates by the seen-cursor, and Clear
 * All targets are already delivered, so they will never be re-delivered (the
 * cursor has already advanced past them).
 */
export async function dismissAllNotifications(
  msgIds: string[],
  conversationKeys: string[]
): Promise<SendResult> {
  try {
    const {
      token,
      androidDeviceId,
      notifications,
      conversations: store,
    } = await webStorage.get("token", "androidDeviceId", "notifications", "conversations");
    if (!token || !androidDeviceId) return { success: false, error: messages.errors.notPaired };

    // Android-side msgIds: regulars mapped through pickAndroidMsgId, conversations
    // via their latest message (no per-thread dismiss endpoint — same as
    // `dismissConversation`).
    const androidMsgIds = msgIds.map((id) => pickAndroidMsgId(id, notifications));
    for (const key of conversationKeys) {
      const messages = store?.[key]?.messages;
      const latest = messages?.[messages.length - 1];
      if (latest) androidMsgIds.push(latest.msgId);
    }

    // Optimistic local removal first, in two bulk writes (regulars + threads).
    await dropNotificationsFromStorage(msgIds);
    if (conversationKeys.length > 0) {
      await conversations.removeConversations(conversationKeys);
    }

    const uniqueAndroidMsgIds = [...new Set(androidMsgIds)];
    if (uniqueAndroidMsgIds.length > 0) {
      await dismissMessages(token, uniqueAndroidMsgIds, androidDeviceId);
    }
    trackDismissIntent(msgIds.length + conversationKeys.length);
    return { success: true };
  } catch (err) {
    logError("messages", "dismiss_all_failed", err);
    return { success: false, error: errorMessage(err) };
  }
}

/**
 * Send an encrypted reply for a notification action.
 *
 * Mirrors the extension's `handleReply`: serialise the ReplyPayload,
 * encrypt under the shared E2E key, base64-encode for transport, and
 * `POST /messages/:msgId/reply`. Any thrown error becomes a
 * `success: false` result so the headless component's reply FSM can
 * surface the message inline rather than killing the React tree.
 */
export async function sendReply(
  msgId: string,
  actionIndex: number,
  text: string
): Promise<SendResult> {
  try {
    const { token, secretKey, phonePubKey, androidDeviceId, notifications } = await webStorage.get(
      "token",
      "secretKey",
      "phonePubKey",
      "androidDeviceId",
      "notifications"
    );
    if (!token || !secretKey || !phonePubKey || !androidDeviceId)
      return { success: false, error: messages.errors.notPaired };

    const reply: ReplyPayload = { type: "reply", actionIndex, text };
    const wire = encryptMessage(
      new TextEncoder().encode(JSON.stringify(reply)),
      decodeBase64(phonePubKey),
      decodeBase64(secretKey)
    );
    const androidMsgId = pickAndroidMsgId(msgId, notifications);
    await replyMessage(token, androidMsgId, encodeBase64(wire), androidDeviceId);
    trackReplySent();
    return { success: true };
  } catch (err) {
    logError("messages", "reply_failed", err);
    return { success: false, error: errorMessage(err) };
  }
}

/**
 * Send an encrypted fire-and-forget action. Same wire shape as reply
 * minus the text body. Server returns 202 once the FCM relay is
 * queued — there's no round-trip ack from Android, so `success: true`
 * here means "server accepted the relay", not "phone executed it".
 */
export async function dispatchAction(msgId: string, actionIndex: number): Promise<SendResult> {
  try {
    const { token, secretKey, phonePubKey, androidDeviceId, notifications } = await webStorage.get(
      "token",
      "secretKey",
      "phonePubKey",
      "androidDeviceId",
      "notifications"
    );
    if (!token || !secretKey || !phonePubKey || !androidDeviceId)
      return { success: false, error: messages.errors.notPaired };

    const action: ActionPayload = { type: "action", actionIndex };
    const wire = encryptMessage(
      new TextEncoder().encode(JSON.stringify(action)),
      decodeBase64(phonePubKey),
      decodeBase64(secretKey)
    );
    const androidMsgId = pickAndroidMsgId(msgId, notifications);
    await actionMessage(token, androidMsgId, encodeBase64(wire), androidDeviceId);
    return { success: true };
  } catch (err) {
    logError("messages", "action_failed", err);
    return { success: false, error: errorMessage(err) };
  }
}
